import { useNavigate } from 'react-router';
import { Widget } from '@typeform/embed-react'
import { useTranslation } from 'react-i18next';
import { useUser } from '@supabase/auth-helpers-react';
import { supabase } from '@/integrations/supabase/client';
import { Spinner } from '@/components/ui/spinner';
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus';
import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const Onboarding = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const user = useUser();
  const { setOnboardedOptimistically } = useOnboardingStatus(user?.id);
  const [submissionError, setSubmissionError] = useState(false);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  if (!user) {
    return (
      <div className="w-full h-[calc(100vh-4rem)] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const handleNewsletterSubscription = () => {
    if (newsletterOptIn && user?.email) {
      toast.loading(t('subscribe.newsletterProcessingTitle', 'Processing...'), {
        description: t('subscribe.newsletterProcessingDesc', 'Subscribing to newsletter...'),
        id: 'newsletter-subscription'
      });
      
      fetch('https://talk.hyvor.com/api/embed/v3/12515/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json', 
        },
        body: JSON.stringify({ 
          email: user.email, 
          segments: [146] 
        }),
      }).then(response => {
        console.log('Newsletter subscription response:', response.status);
        if (!response.ok) {
          throw new Error(`Status: ${response.status}`);
        }
        toast.success(t('subscribe.newsletterSuccessTitle', 'Newsletter Subscription'), {
          description: t('subscribe.newsletterSuccessDesc', 'You have been subscribed to our newsletter.'),
          duration: 5000,
          id: 'newsletter-subscription'
        });
      }).catch(error => {
        console.error('Newsletter subscription error:', error);
        toast.error(t('subscribe.newsletterErrorTitle', 'Subscription Error'), {
          description: t('subscribe.newsletterErrorApiFail', 'An error occurred while processing your subscription.'),
          duration: 5000,
          id: 'newsletter-subscription'
        });
      });
    }
  };

  const proceedToTypeform = () => {
    handleNewsletterSubscription();
    setCurrentStep(2);
  };

  return (
    <div className="w-full h-[calc(100vh-4rem)] flex flex-col items-center justify-start p-4">
      <div className="w-full max-w-2xl space-y-8">
        <h1 className="text-3xl font-bold text-center">{t('form.title', 'Onboarding Questions')}</h1>
        
        {submissionError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{t('error.title', 'Error')}</h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{t('error.onboarding', 'Failed to update onboarding status')}</p>
                </div>
                <div className="mt-4">
                </div>
              </div>
            </div>
          </div>
        )}
        
        {currentStep === 1 && (
          <div className="flex flex-col space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-6">
              <h2 className="text-xl font-medium mb-4">{t('subscribe.newsletterTitle', 'Newsletter Subscription')}</h2>
              <p className="text-gray-700 mb-6">{t('subscribe.newsletterDescription', 'Would you like to stay updated with our latest news and features?')}</p>
              
              <div className="flex items-center space-x-3 mb-8">
                <Checkbox
                  id="newsletter-opt-in"
                  checked={newsletterOptIn}
                  onCheckedChange={(checked) => setNewsletterOptIn(Boolean(checked))}
                  aria-labelledby="newsletter-opt-in-label"
                />
                <label
                  htmlFor="newsletter-opt-in"
                  id="newsletter-opt-in-label"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t('subscribe.newsletterOptInLabel', 'Receive news and updates via email')}
                </label>
              </div>
              
              <Button 
                className="w-full" 
                onClick={proceedToTypeform}
              >
                {t('button.continue', "Continue")}
              </Button>
            </div>
          </div>
        )}
        
        {currentStep === 2 && (
          <Widget
            id={i18n.language === 'ru' ? 'IlZiGix3' : 'XSdLHgHl'}
            style={{ height: submissionError ? '50vh' : '60vh' }}
            className="my-form"
            hidden={{ user_id: user?.id, email: user?.email }}
            onSubmit={async () => {
              handleFormSubmit();
            }}
            onReady={() => {}}
          />
        )}
      </div>
    </div>
  );

  async function handleFormSubmit() {
    try {
      setOnboardedOptimistically();
      navigate('/tutorial/rwx9san73ae1tm00cxkyh06d'); 
    } catch (error) {
      toast.error(t('error.title', 'Error'), {
        description: t('error.onboarding', 'Failed to update onboarding status'),
        duration: 5000
      });
    }
  }
};

export default Onboarding;
